import { memo, useRef, useEffect } from 'react';
import { App } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useRequest } from '@wappsto/blanket';
import { useTranslation } from 'translations';
import { METHODS } from 'util/params';

const TYPE_DELETE = 'delete';
const TYPE_ACCOUNT = 'account';
const TYPE_REGISTRATION = 'registration';

interface ConfirmationModalProps {
  isRedirectingToMain?: boolean;
}

const ConfirmationModal = memo(({ isRedirectingToMain = false }: ConfirmationModalProps) => {
  const { tC } = useTranslation('features', 'confirmationModal');
  const { request: confirmationRequest, send } = useRequest();
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const requestParam = urlParams.get('request');
  const { modal } = App.useApp();
  const modalRef = useRef<ReturnType<typeof modal.info> | null>(null);
  const type = useRef<string | null>(null);
  let message: any;

  if (
    confirmationRequest &&
    confirmationRequest.status === 'error' &&
    confirmationRequest.json &&
    confirmationRequest.json.code
  ) {
    message = tC(`error:${confirmationRequest.json.code}`, confirmationRequest.json.data);
  }

  useEffect(() => {
    const start = (ct, method) => {
      type.current = ct;
      send({
        url: `/register${location.search}`,
        method,
      });
      if (isRedirectingToMain) {
        navigate('', {});
      }
    };

    switch (requestParam) {
      case 'confirm_deletion_user':
        start(TYPE_DELETE, METHODS.delete);
        break;
      case 'confirm_changed_email':
        start(TYPE_ACCOUNT, METHODS.get);
        break;
      case 'confirm_new_username':
        start(TYPE_ACCOUNT, METHODS.get);
        break;
      case 'confirm_registration':
        start(TYPE_REGISTRATION, METHODS.get);
        break;
      default:
        break;
    }
  }, [requestParam, isRedirectingToMain, send, navigate, location.search]);

  useEffect(() => {
    if (confirmationRequest && confirmationRequest.url === '/register') {
      switch (confirmationRequest.status) {
        case 'error':
          modalRef.current?.update({
            maskClosable: false,
            icon: <CloseCircleOutlined style={{ color: 'red' }} />,
            content:
              message ||
              tC(type.current === TYPE_DELETE ? 'deleteAccountError' : 'emailConfirmationError'),
            okButtonProps: {
              style: {
                display: 'block',
              },
            },
          });
          break;
        case 'success':
          modalRef.current?.update({
            maskClosable: false,
            icon: <CheckCircleOutlined style={{ color: 'green' }} />,
            content: tC(
              type.current === TYPE_DELETE ? 'deleteAccountSuccess' : 'emailConfirmationSuccess',
            ),
            okButtonProps: {
              style: {
                display: 'block',
              },
            },
          });
          break;
        default: // 'pending':
          modalRef.current = modal.info({
            maskClosable: false,
            icon: <LoadingOutlined />,
            content: tC(
              type.current === TYPE_DELETE ? 'deleteAccountPending' : 'emailConfirmationPending',
            ),
            okButtonProps: {
              style: {
                display: 'none',
              },
            },
          });
          break;
      }
    }
  }, [confirmationRequest, message, tC, modal]);

  return null;
});

ConfirmationModal.displayName = 'ConfirmationModal';

export default ConfirmationModal;
